// ==============================================================================
// FILE: src/pages/ClaimReview-Page.tsx (FEATURE: Multi-Receipt Link)
//
// ... (changes 1-7) ...
// CHANGE 8 (FIX): Corrected import paths and removed unused code to fix
//           build errors.
// CHANGE 9 (FIX): Corrected selection-jumping bug by ensuring auto-selection
//           logic only runs once on the initial load.
// ==============================================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, List, ListItem, ListItemButton,
  ListItemText, TextField, Button as MuiButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip, Divider,
  FormControl, InputLabel, Select, MenuItem,
  Accordion, AccordionSummary, AccordionDetails,
  // --- CHANGE: Added Checkbox and ListItemIcon ---
  Checkbox,
  Button,
  ListItemIcon
} from '@mui/material';
import {
    ArrowBack, CheckCircleOutline, HighlightOff,
    Visibility, HelpOutline, Edit as EditIcon,
    PublishedWithChanges as ReopenIcon, // Batch Reopen Icon
    ExpandMore as ExpandMoreIcon,
    PriceCheck as PriceCheckIcon,
    Undo as UndoIcon // Item Reopen Icon
} from '@mui/icons-material';
// --- FIX: Use correct relative paths ---
import { useAppContext } from '../contexts/AppContext';
import {
  ReviewQueueDetail, ClaimResult, ClaimItem, MatchStatus, UnmatchedReceipt, ClaimResultUpdate,
  // --- CHANGE: Added/Removed types ---
  LinkedReceipt, LinkMultipleReceiptsRequest
} from '../types';

const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
};

const statusChipColor = (status: MatchStatus): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
        case 'matched':
        case 'manually_approved':
            return 'success';
        case 'unmatched':
            return 'error';
        case 'human_intervention':
            return 'warning';
        default:
            return 'default';
    }
};

interface SelectedItemState {
    result: ClaimResult;
    original: ClaimItem;
}

interface ClaimReviewPageProps {
  batchId: string;
}

export default function ClaimReviewPage({ batchId }: ClaimReviewPageProps): JSX.Element {
  const { apiService, setActivePage, setReviewingBatchId, showNotification, isSuperAdmin } = useAppContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<ReviewQueueDetail | null>(null);
  const [selected, setSelected] = useState<SelectedItemState | null>(null);
  const [unmatchedReceipts, setUnmatchedReceipts] = useState<UnmatchedReceipt[]>([]);
  
  // --- CHANGE: Removed single-link modal state ---
  // const [isSingleLinkModalOpen, setIsSingleLinkModalOpen] = useState<boolean>(false);
  // --- CHANGE: Added multi-link modal state ---
  const [isMultiLinkModalOpen, setIsMultiLinkModalOpen] = useState<boolean>(false);
  // --- CHANGE: Added multi-select state ---
  const [selectedReceipts, setSelectedReceipts] = useState<Set<string>>(new Set());

  const [selectedUnmatchedReceipt, setSelectedUnmatchedReceipt] = useState<UnmatchedReceipt | null>(null);
  const [receiptUrl, setReceiptUrl] = useState<string>('');
  const [receiptLoading, setReceiptLoading] = useState<boolean>(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewedExtractedData, setPreviewedExtractedData] = useState<any | null>(null);
  const [editableAmount, setEditableAmount] = useState<string>('');
  const [editableDate, setEditableDate] = useState<string>('');
  const [editableExpenseType, setEditableExpenseType] = useState<string>('');
  const [editableBillNo, setEditableBillNo] = useState<string>('');
  const [isReceiptProvided, setIsReceiptProvided] = useState<boolean>(false);
  const [batchStatus, setBatchStatus] = useState<string>('review_required');
  const [initialStatus, setInitialStatus] = useState<string | null>(null);
  // --- NEW STATE VARIABLES ---
  const [editableTaxableAmount, setEditableTaxableAmount] = useState<string>('');
  const [editableNonTaxableAmount, setEditableNonTaxableAmount] = useState<string>('');

  const [pendingActions, setPendingActions] = useState<Record<string, boolean>>({});

  const hasChanges = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const findOriginalItem = useCallback((result: ClaimResult, allItems: ClaimItem[]): ClaimItem | undefined => {
    return allItems.find(item => item.original_row_index === result.claim_id);
  }, []);

  // --- User selection ---
  const handleSelectItem = (result: ClaimResult) => {
    console.log("handleSelectItem triggered for:", result.id);
    if (!data) return;
    const original = findOriginalItem(result, data.claim_items);
    if (original) {
        // Only update state if the selection actually changed
        if (!selected || selected.result.id !== result.id) {
            setSelected({ result, original });
            setPreviewedExtractedData(null); // Clear preview data when selecting a new item
            // --- CHANGE: Clear multi-select when selecting a new item ---
            setSelectedReceipts(new Set());
        }
    }
  };

  const fetchBatchStatus = useCallback(async () => {
    try {
        const response = await apiService.getBatchStatus(batchId);
        setBatchStatus(response.status);
    } catch (err) {
        console.error("Failed to fetch batch status:", err);
        // Avoid stopping the interval on a single failed fetch
    }
  }, [apiService, batchId]);

  // --- Fetching data and refined auto-selection ---
  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) setLoading(true); // Only show main loader on initial load
    setError(null);
    try {
        const [detailsData, unmatchedData, batchStatusData] = await Promise.all([
            apiService.getReviewDetails(batchId),
            apiService.getUnmatchedReceipts(batchId),
            apiService.getBatchStatus(batchId)
        ]);
        setData(detailsData);
        setUnmatchedReceipts(unmatchedData);
        setBatchStatus(batchStatusData.status);

        // Store the initial status only once
        if (initialStatus === null) {
            setInitialStatus(batchStatusData.status);
        }

        // --- AUTO-SELECTION: Only on initial page load ---
        if (isInitialLoad) {
            const firstReviewItem = detailsData.claim_results.find(
                (r: ClaimResult) => r.is_approved_by_reviewer === null && r.match_status !== 'matched'
            );
            let itemToSelect = firstReviewItem;
            if (!itemToSelect && detailsData.claim_results.length > 0) {
                 itemToSelect = detailsData.claim_results[0];
            }
            if (itemToSelect) {
                const original = findOriginalItem(itemToSelect, detailsData.claim_items);
                if (original) {
                    setSelected({ result: itemToSelect, original });
                }
            }
        }
        // --- END AUTO-SELECTION ---


    } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch claim details.';
        setError(errorMessage);
        if (isInitialLoad) showNotification(errorMessage, 'error'); // Show error only on initial load fail
    } finally {
        if (isInitialLoad) setLoading(false);
    }
  // --- FIX: Removed 'selected' from dependency array to stop re-running ---
  }, [apiService, batchId, showNotification, findOriginalItem, initialStatus]);


  // Effect for initial data load
  useEffect(() => {
    fetchData(true); // Pass true for initial load
  // --- FIX: Removed `fetchData` from dep array and added eslint-disable ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Effect for polling batch status
  useEffect(() => {
    let shouldPoll = initialStatus === 'review_required' || initialStatus === null;
    if (shouldPoll && !intervalRef.current) {
        console.log("Starting batch status polling...");
        intervalRef.current = setInterval(async () => { // Make interval async
             console.log("Polling batch status...");
             await fetchBatchStatus();
             // Also refetch item data periodically if still in review
             if (batchStatus === 'review_required') {
                 console.log("Polling item data...");
                 await fetchData(false); // Fetch item data without full loading spinner
             }
        }, 7000); // Poll slightly less frequently
    } else if (!shouldPoll && intervalRef.current) {
        console.log("Stopping batch status polling.");
        clearInterval(intervalRef.current);
        intervalRef.current = null;
    }

    // Cleanup interval on unmount
    return () => {
        if (intervalRef.current) {
            console.log("Clearing polling interval on unmount.");
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };
  // Depend on initialStatus AND batchStatus to manage polling lifecycle
  }, [fetchBatchStatus, fetchData, initialStatus, batchStatus]);

  // Effect for handling redirection when batch status becomes final
  useEffect(() => {
    const finalStatuses = ['completed', 'partially_approved', 'rejected'];
    // Redirect only if the status transitions TO a final state FROM review_required
    if (finalStatuses.includes(batchStatus) && initialStatus === 'review_required') {
        showNotification('Batch has been finalized! Redirecting to Claim History...', 'success');
        if (intervalRef.current) { // Stop polling
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setTimeout(() => {
            setReviewingBatchId(null);
            setActivePage('Claim History');
        }, 2000);
    }
  }, [batchStatus, initialStatus, setReviewingBatchId, setActivePage, showNotification]);

  // Effect to update UI elements when the 'selected' item changes
  useEffect(() => {
    const updateSelectionDetails = async () => {
        if (!selected) {
            setReceiptUrl('');
            setNotes('');
            // Clear editable fields when nothing is selected
            setEditableAmount('');
            setEditableDate('');
            setEditableExpenseType('');
            setEditableBillNo('');
            setIsReceiptProvided(false);
            setEditableTaxableAmount('');
            setEditableNonTaxableAmount('');
            return;
        }
        hasChanges.current = false; // Reset changes flag on selection change

        const { result, original } = selected;
        console.log("updateSelectionDetails running for:", result.id);
        setReceiptUrl(''); // Clear previous receipt URL
        setReceiptLoading(true);

        // Fetch receipt preview URL if available
        if (result.receipt_file_path && result.receipt_file_name) {
            try {
                const response = await apiService.getReceiptPreviewUrl(result.id);
                let url = response.url;
                // Append page number for PDFs
                if (result.receipt_file_name.toLowerCase().endsWith('.pdf') && result.page_number) {
                    url += `#page=${result.page_number}`;
                }
                setReceiptUrl(url);
            } catch (error) {
                console.error("API Error fetching signed URL:", error);
                showNotification('Failed to fetch receipt URL.', 'error');
                setReceiptUrl(''); // Clear URL on error
            } finally {
               setReceiptLoading(false);
            }
        } else {
             setReceiptLoading(false); // No receipt path, stop loading
        }


        // Populate editable fields based on the selected item's data
        setNotes(result.notes || '');
        const receiptData = result.matched_receipt_info;
        // Prioritize overridden values, then receipt data, then original claim item data
        setEditableAmount(result.overridden_total_item_amount?.toString() || receiptData?.total?.toString() || original.total_item_amount?.toString() || '');
        setEditableExpenseType(result.overridden_expense_type || receiptData?.bill_type || original.expense_type || '');
        setEditableBillNo(result.overridden_bill_no || receiptData?.bill_no || original.bill_no || '');
        try {
            const rawDate = result.overridden_expense_date || receiptData?.date || original.expense_date;
            // Ensure date is formatted correctly for the input type="date"
            const formattedDate = rawDate ? new Date(rawDate).toISOString().split('T')[0] : '';
            setEditableDate(formattedDate);
        } catch {
            setEditableDate(''); // Fallback to empty string if date parsing fails
        }
        // Determine isReceiptProvided status (defaulting based on presence of receipt path)
        setIsReceiptProvided(result.is_receipt_provided ?? (!!result.receipt_file_path));
        // Populate tax amounts
        setEditableTaxableAmount(result.taxable_amount?.toString() || '');
        setEditableNonTaxableAmount(result.non_taxable_amount?.toString() || '');
    };

    updateSelectionDetails();
  }, [selected, apiService, showNotification]); // Rerun when 'selected' changes

  // Function to save changes automatically on blur (if changes were made)
  const handleAutoSaveChanges = useCallback(async () => {
    if (!selected || !hasChanges.current) return; // Only save if selected and changes exist

    // Construct payload with current values from state
    const payload: ClaimResultUpdate = {
        notes: notes,
        // Convert amount back to number, handle potential NaN
        overridden_total_item_amount: !isNaN(parseFloat(editableAmount)) ? parseFloat(editableAmount) : undefined,
        overridden_expense_date: editableDate || undefined, // Send if not empty
        overridden_expense_type: editableExpenseType || undefined,
        overridden_bill_no: editableBillNo || undefined,
        is_receipt_provided: isReceiptProvided,
        taxable_amount: !isNaN(parseFloat(editableTaxableAmount)) ? parseFloat(editableTaxableAmount) : undefined,
        non_taxable_amount: !isNaN(parseFloat(editableNonTaxableAmount)) ? parseFloat(editableNonTaxableAmount) : undefined,
    };

    const resultId = selected.result.id;
    // Set pending state specific to saving changes
    const saveKey = `${resultId}_save`;
    setPendingActions(prev => ({ ...prev, [saveKey]: true }));

    try {
        console.log("[AUTO-SAVE] Request payload:", JSON.stringify(payload, null, 2));
        const updatedResult = await apiService.updateClaimResult(resultId, payload);
        console.log("[AUTO-SAVE] Response:", JSON.stringify({ taxable_amount: updatedResult.taxable_amount, non_taxable_amount: updatedResult.non_taxable_amount }, null, 2));
        // Update local state with the saved data from the API response
        setData((prevData: ReviewQueueDetail | null) => {
            if (!prevData) return null;
            const newResults = prevData.claim_results.map((r: ClaimResult) => r.id === updatedResult.id ? updatedResult : r);
            return { ...prevData, claim_results: newResults };
        });
        // Update the 'selected' state as well to reflect saved changes
        setSelected(prev => prev ? { ...prev, result: updatedResult } : null);
        showNotification('Changes saved automatically.', 'success');
        hasChanges.current = false; // Reset changes flag after successful save
    } catch (err: any) {
        showNotification(err.message || 'Failed to auto-save changes.', 'error');
        // Optionally revert UI changes or re-fetch data on save failure
    } finally {
        // Clear pending state for saving changes
        setPendingActions(prev => {
            const newState = {...prev};
            delete newState[saveKey];
            return newState;
        });
    }
  }, [selected, notes, editableAmount, editableDate, editableExpenseType, editableBillNo, isReceiptProvided, apiService, showNotification, editableTaxableAmount, editableNonTaxableAmount]);

  // Handle Approve/Reject actions
  const handleReviewAction = async (isApproved: boolean) => {
    if (!selected) return;

    const resultId = selected.result.id;
    setPendingActions(prev => ({ ...prev, [resultId]: true })); // Use resultId as key for pending state

    try {
        // Ensure latest changes are saved before the final approve/reject action
        if (hasChanges.current) {
            await handleAutoSaveChanges();
        }

        // Send the final approval status along with potentially updated notes/receipt status
        const updatedResult = await apiService.updateClaimResult(resultId, {
            is_approved_by_reviewer: isApproved,
            notes: notes, // Send current notes state
            is_receipt_provided: isReceiptProvided, // Send current receipt status
        });

        // Update local state immediately based on API response
        setData((prevData: ReviewQueueDetail | null) => {
            if (!prevData) return null;
            const newResults = prevData.claim_results.map((r: ClaimResult) =>
                r.id === resultId ? updatedResult : r
            );
            return { ...prevData, claim_results: newResults };
        });
        // Update the 'selected' item state to reflect the action
        setSelected(prev => prev ? { ...prev, result: updatedResult } : null);

        showNotification(`Item ${isApproved ? 'approved' : 'rejected'}.`, 'success');

    } catch (err: any) {
        showNotification(err.message || 'Failed to save action.', 'error');
        // Consider re-fetching data or providing a retry mechanism on failure
    } finally {
        // Clear pending state for this specific item's action
        setPendingActions((prev: Record<string, boolean>) => {
            const newActions = { ...prev };
            delete newActions[resultId];
            return newActions;
        });
    }
  };

  // Function to handle re-opening an item (resetting approval status)
  const handleReopenItem = async () => {
    if (!selected) return;

    const resultId = selected.result.id;
    const reopenKey = `${resultId}_reopen`; // Unique key for reopen pending state
    setPendingActions(prev => ({ ...prev, [reopenKey]: true }));

    try {
      // Call backend API to set is_approved_by_reviewer back to undefined/null
      // The backend needs to handle this PATCH request correctly
      const updatedResult = await apiService.updateClaimResult(resultId, {
        is_approved_by_reviewer: undefined, // Send undefined to signal reset
      });

      // Update local state immediately
      setData((prevData: ReviewQueueDetail | null) => {
        if (!prevData) return null;
        const newResults = prevData.claim_results.map((r: ClaimResult) =>
          r.id === resultId ? updatedResult : r
        );
        // Important: Revert Batch Status in local state if needed
        // This assumes backend handles the actual DB update for batch status
        let newBatchStatus = batchStatus;
        const finalStatuses = ['completed', 'partially_approved', 'rejected'];
        if (finalStatuses.includes(batchStatus)) {
            newBatchStatus = 'review_required';
            setBatchStatus(newBatchStatus); // Update local batch status state
             // Restart polling if it was stopped
            if (!intervalRef.current) {
                intervalRef.current = setInterval(async () => { // Make interval async
                    await fetchBatchStatus();
                    // Check updated status after fetching
                    if (newBatchStatus === 'review_required') {
                        await fetchData(false);
                    }
                }, 7000);
            }
        }
        return { ...prevData, claim_results: newResults };
      });
      // Update the 'selected' state to reflect the reset, enabling fields/buttons
      setSelected(prev => prev ? { ...prev, result: updatedResult } : null);


      showNotification('Item status reset. You can now make corrections.', 'info');

    } catch (err: any) {
      showNotification(err.message || 'Failed to re-open item.', 'error');
    } finally {
      // Clear the specific pending state for re-opening this item
      setPendingActions((prev: Record<string, boolean>) => {
        const newActions = { ...prev };
        delete newActions[reopenKey];
        return newActions;
      });
    }
  };

  // --- CHANGE: Removed function ---
  // const handleOpenSingleLinkModal = (receipt: UnmatchedReceipt) => { ... };

  // --- CHANGE: Removed function ---
  // const handleCloseSingleLinkModal = () => { ... };

  // --- CHANGE: Removed function ---
  // const handleSingleLinkSubmit = async () => { ... };

  // --- CHANGE: Added new function (Corrected) ---
  // Open the modal for linking MULTIPLE receipts
  const handleOpenMultiLinkModal = () => {
    if (!selected) return;

    let calculatedTotal = 0;
    unmatchedReceipts.forEach(receipt => {
        if (selectedReceipts.has(receipt.id)) {
            calculatedTotal += receipt.extracted_data?.total || 0;
        }
    });

    // Pre-fill amount and notes only, per the simplified plan
    setEditableAmount(calculatedTotal > 0 ? calculatedTotal.toString() : (selected.original.total_item_amount?.toString() || ''));
    setNotes(''); 
    
    // Clear other fields as they are not used in this modal
    setEditableExpenseType('');
    setEditableDate('');
    setEditableBillNo('');
    setEditableTaxableAmount('');
    setEditableNonTaxableAmount('');

    setIsMultiLinkModalOpen(true);
  };

  // --- CHANGE: Added new function ---
  const handleCloseMultiLinkModal = () => {
    setIsMultiLinkModalOpen(false);
    // Note: We don't clear selectedReceipts here, user might just be closing to check something
  };

  // --- CHANGE: Added new function ---
  const handleMultiLinkSubmit = async () => {
    if (!selected || selectedReceipts.size === 0) return;

    const resultId = selected.result.id;
    setPendingActions(prev => ({ ...prev, [resultId]: true }));
    setIsMultiLinkModalOpen(false);

    // Use the simplified payload as defined in the implementation plan
    const simplePayload: LinkMultipleReceiptsRequest = {
       receipt_ids: Array.from(selectedReceipts),
       notes: notes || undefined,
       overridden_total_item_amount: !isNaN(parseFloat(editableAmount)) ? parseFloat(editableAmount) : undefined,
       taxable_amount: !isNaN(parseFloat(editableTaxableAmount)) ? parseFloat(editableTaxableAmount) : undefined,
       non_taxable_amount: !isNaN(parseFloat(editableNonTaxableAmount)) ? parseFloat(editableNonTaxableAmount) : undefined,
    };

    try {
        await apiService.linkMultipleReceipts(resultId, simplePayload);
        showNotification('Receipts linked successfully!', 'success');
        setSelectedReceipts(new Set()); // Clear selection
        await fetchData(false); // Re-fetch all data
    } catch (err: any) {
        showNotification(err.message || 'Failed to link receipts.', 'error');
    } finally {
        setPendingActions(prev => {
            const newActions = { ...prev };
            delete newActions[resultId];
            return newActions;
        });
    }
  };

  // Preview an unmatched receipt
  const handlePreviewUnmatched = async (receiptId: string) => {
    const receiptToPreview = unmatchedReceipts.find(r => r.id === receiptId);
    // Display extracted data from the specific unmatched receipt being previewed
    if (receiptToPreview) {
        setPreviewedExtractedData(receiptToPreview.extracted_data);
    } else {
        setPreviewedExtractedData(null); // Clear if not found
    }

    setReceiptUrl(''); // Clear previous URL
    setReceiptLoading(true);
    try {
        const res = await apiService.getUnmatchedReceiptPreviewUrl(receiptId);
        let url = res.url;
        // Append page number for PDFs
        if (receiptToPreview?.file_name?.toLowerCase().endsWith('.pdf') && receiptToPreview.page_number) {
            url += `#page=${receiptToPreview.page_number}`;
        }
        setReceiptUrl(url);
    } catch {
        showNotification('Could not load receipt preview.', 'error');
        setReceiptUrl(''); // Clear URL on error
        setPreviewedExtractedData(null); // Clear extracted data view on error
    } finally {
        setReceiptLoading(false);
    }
  };

  // --- CHANGE: Added new function ---
  const handlePreviewLinkedReceipt = async (receipt: LinkedReceipt) => {
    setPreviewedExtractedData(receipt.extracted_data); // Show its data
    setReceiptUrl('');
    setReceiptLoading(true);
    try {
        // Use the correct new API function
        const res = await apiService.getLinkedReceiptPreviewUrl(receipt.id);
        let url = res.url;
        if (receipt.file_name?.toLowerCase().endsWith('.pdf') && receipt.page_number) {
            url += `#page=${receipt.page_number}`;
        }
        setReceiptUrl(url);
    } catch (err: any) {
        showNotification(err.message || 'Could not load receipt preview.', 'error');
        setReceiptUrl('');
        setPreviewedExtractedData(null);
    } finally {
        setReceiptLoading(false);
    }
  };


  // Check if the overall batch can be re-opened (SuperAdmin role required)
  const canReopenBatch = isSuperAdmin() && ['completed', 'partially_approved', 'rejected'].includes(batchStatus);
  // Check if the batch is in a final state (used for UI logic like disabling re-open item)
  const isBatchFinalized = ['completed', 'partially_approved', 'rejected'].includes(batchStatus);

  // Handle re-opening the entire batch (SuperAdmin action)
  const handleReopenClaimBatch = async () => {
    if (!batchId) return;
    setPendingActions(prev => ({ ...prev, reopenBatch: true })); // Set pending state for batch reopen
    try {
      await apiService.reopenClaim(batchId);
      showNotification('Claim successfully re-opened. Redirecting...', 'success');
      // Redirect after a short delay
      setTimeout(() => {
        setReviewingBatchId(null); // Clear the reviewing ID
        setActivePage('Claim History'); // Navigate away
      }, 2000);
    } catch (err: any) {
      showNotification(err.message || 'Failed to re-open claim.', 'error');
      // Clear pending state on error
      setPendingActions(prev => { const newActions = {...prev}; delete newActions.reopenBatch; return newActions; });
    }
    // No finally needed here as success causes navigation
  };

  // Helper component for displaying comparison rows
  const ComparisonRow = ({ label, claimedValue, extractedValue }: { label: string, claimedValue: any, extractedValue: any }) => (
    <>
        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
        <Grid item xs={5}><Typography color="text.secondary" variant="body2">{label}</Typography></Grid>
        <Grid item xs={3.5}><Typography variant="body2" align="right">{claimedValue ?? 'N/A'}</Typography></Grid>
        <Grid item xs={3.5}><Typography variant="body2" align="right"><strong>{extractedValue ?? 'N/A'}</strong></Typography></Grid>
    </>
  );

  // Helper component for displaying the financial summary
  const TaxSummaryPanel = ({ result }: { result: ClaimResult }) => {
    // Determine the final approved amount, prioritizing overrides, then matched info, else 0
    const finalApprovedAmount = result.overridden_total_item_amount ?? result.matched_receipt_info?.total ?? 0;


    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, backgroundColor: 'grey.50' }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <PriceCheckIcon sx={{ mr: 1, color: 'primary.main' }} />
                Financial Summary
            </Typography>
            <Grid container spacing={1}>
                <Grid item xs={8}><Typography color="text.secondary">Non-Taxable Amount:</Typography></Grid>
                <Grid item xs={4} sx={{ textAlign: 'right' }}><Typography sx={{ fontWeight: 'bold' }}>{formatCurrency(result.non_taxable_amount)}</Typography></Grid>
                <Grid item xs={8}><Typography color="text.secondary">Taxable Amount:</Typography></Grid>
                <Grid item xs={4} sx={{ textAlign: 'right' }}><Typography sx={{ fontWeight: 'bold', color: result.taxable_amount && result.taxable_amount > 0 ? 'error.main' : 'inherit' }}>{formatCurrency(result.taxable_amount)}</Typography></Grid>
                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                <Grid item xs={8}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>Approved Amount:</Typography></Grid>
                <Grid item xs={4} sx={{ textAlign: 'right' }}><Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCurrency(finalApprovedAmount)}</Typography></Grid>
            </Grid>
        </Paper>
    );
  }

  // Main render function for the details panel based on the selected item
  const renderDetailsPanel = () => {
    if (!selected) {
        return <Box sx={{ p: 2, textAlign: 'center' }}>Select an item to review its details.</Box>;
    }
    const { result: selectedItem, original: originalSelectedItem } = selected;
    const isActioned = selectedItem.is_approved_by_reviewer !== null;
    const isAiMatched = selectedItem.match_status === 'matched';
    // Check pending state for *any* action on this item
    const isActionPending = pendingActions[selectedItem.id] || pendingActions[`${selectedItem.id}_save`] || pendingActions[`${selectedItem.id}_reopen`];
    // Use previewed data if available (from unmatched preview), otherwise use matched info
    const receiptData = previewedExtractedData || selectedItem.matched_receipt_info;

    // Determine if correction fields should be editable:
    // Fields are editable if the item has NOT been actioned OR AI matched
    const isEditable = !isActioned && !isAiMatched;
    // Notes are editable unless an action is pending
    const isNotesEditable = !isActionPending;

    return (
        <Box>
            {/* Header with Item Description and Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ flexGrow: 1, m: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {originalSelectedItem.description || 'No Description'}
                </Typography>
                <Box sx={{ display: 'flex', flexShrink: 0, position: 'relative', minWidth: '80px', justifyContent: 'flex-end' }}>
                     {/* Loading spinner if any action is pending */}
                     {isActionPending && <CircularProgress size={32} sx={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)' }} />}

                     {/* Conditional Button Rendering */}
                     {!isActioned && !isAiMatched && ( // Show Approve/Reject if editable
                        <>
                            <Tooltip title="Reject"><span><IconButton onClick={() => handleReviewAction(false)} disabled={isActionPending} color="error"><HighlightOff /></IconButton></span></Tooltip>
                            <Tooltip title="Approve"><span><IconButton onClick={() => handleReviewAction(true)} disabled={isActionPending} color="success" sx={{ pr: 0 }}><CheckCircleOutline /></IconButton></span></Tooltip>
                        </>
                     )}
                     {/* --- THIS IS THE FIX --- */}
                     {/* Show Re-open if actioned or AI matched, regardless of batch status */}
                     {(isActioned || isAiMatched) && (
                        <Tooltip title="Re-open item to make corrections"><span><IconButton onClick={handleReopenItem} disabled={isActionPending} color="primary" sx={{ pr: 0 }}><UndoIcon /></IconButton></span></Tooltip>
                     )}
                     {/* --- END FIX --- */}
                </Box>
            </Box>

            {/* --- CHANGE: Conditional Comparison Grid / Linked Receipts List --- */}
            {selectedItem.match_status !== 'manually_approved' ? (
                <Grid container spacing={1} component={Paper} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Grid item xs={5}><Typography variant="body2" sx={{ fontWeight: 'bold' }}>Field</Typography></Grid>
                    <Grid item xs={3.5}><Typography variant="body2" sx={{ fontWeight: 'bold' }} align="right">Claimed</Typography></Grid>
                    <Grid item xs={3.5}><Typography variant="body2" sx={{ fontWeight: 'bold' }} align="right">Extracted</Typography></Grid>
                    <ComparisonRow label="Expense Type" claimedValue={originalSelectedItem.expense_type} extractedValue={receiptData?.bill_type} />
                    <ComparisonRow label="Bill No." claimedValue={originalSelectedItem.bill_no} extractedValue={receiptData?.bill_no} />
                    <ComparisonRow label="Date" claimedValue={originalSelectedItem.expense_date ? new Date(originalSelectedItem.expense_date).toLocaleDateString() : 'N/A'} extractedValue={receiptData?.date ? new Date(receiptData.date).toLocaleDateString() : 'N/A'} />
                    <ComparisonRow label="Amount" claimedValue={formatCurrency(originalSelectedItem.total_item_amount)} extractedValue={formatCurrency(receiptData?.total)} />
                </Grid>
            ) : (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Linked Receipts</Typography>
                    <List dense>
                        {data?.linked_receipts?.filter(r => r.claim_result_id === selectedItem.id).map(linked => (
                            <ListItem key={linked.id} secondaryAction={
                                <IconButton edge="end" onClick={() => handlePreviewLinkedReceipt(linked)}>
                                    <Visibility />
                                </IconButton>
                            }>
                                <ListItemText
                                    primary={linked.file_name}
                                    secondary={`Amount: ${formatCurrency(linked.extracted_data?.total)}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}
            {/* --- END CHANGE --- */}


            {/* Financial Summary (shown if actioned or AI matched) */}
            {(isActioned || isAiMatched) && (<TaxSummaryPanel result={selectedItem} />)}

            {/* AI Explanation Alerts */}
            {selectedItem.match_status === 'human_intervention' && (<Alert severity="warning" icon={<EditIcon fontSize="inherit" />} sx={{ my: 2 }}>{selectedItem.llm_explanation || "AI requires verification."}</Alert>)}
            {selectedItem.match_status === 'matched' && (<Alert severity="success" sx={{ my: 2 }}>{selectedItem.llm_explanation || "AI successfully matched this item."}</Alert>)}
            {selectedItem.match_status === 'unmatched' && (<Alert severity="error" sx={{ my: 2 }}>{selectedItem.llm_explanation || "AI could not find a matching receipt."}</Alert>)}

            {/* Manual Correction Accordion - Conditionally rendered and fields conditionally disabled */}
            {/* Show accordion if item needs intervention, is unmatched, OR HAS BEEN RE-OPENED (isEditable is true) */}
            {(selectedItem.match_status === 'human_intervention' || selectedItem.match_status === 'unmatched' || isEditable) && (
                 <Accordion defaultExpanded={false} sx={{ boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Manual Correction & Notes</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            {/* Fields are disabled if not editable OR action pending */}
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Corrected Expense Type" value={editableExpenseType} onChange={(e) => {setEditableExpenseType(e.target.value); hasChanges.current = true;}} onBlur={handleAutoSaveChanges} disabled={!isEditable || isActionPending} size="small" /></Grid>
                            {/* --- THIS IS THE SYNTAX FIX --- */}
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Corrected Amount" value={editableAmount} onChange={(e) => {setEditableAmount(e.target.value); hasChanges.current = true;}} onBlur={handleAutoSaveChanges} disabled={!isEditable || isActionPending} size="small" type="number" helperText={(editableTaxableAmount || editableNonTaxableAmount) && Math.abs((parseFloat(editableAmount) || 0) - ((parseFloat(editableTaxableAmount) || 0) + (parseFloat(editableNonTaxableAmount) || 0))) > 0.01 ? "Total doesn't match Taxable + Non-Taxable" : ""} error={!!(editableTaxableAmount || editableNonTaxableAmount) && Math.abs((parseFloat(editableAmount) || 0) - ((parseFloat(editableTaxableAmount) || 0) + (parseFloat(editableNonTaxableAmount) || 0))) > 0.01} /></Grid>
                            {/* --- END SYNTAX FIX --- */}
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Corrected Date" type="date" value={editableDate} onChange={(e) => {setEditableDate(e.target.value); hasChanges.current = true;}} onBlur={handleAutoSaveChanges} InputLabelProps={{ shrink: true }} disabled={!isEditable || isActionPending} size="small" /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Corrected Bill No." value={editableBillNo} onChange={(e) => {setEditableBillNo(e.target.value); hasChanges.current = true;}} onBlur={handleAutoSaveChanges} disabled={!isEditable || isActionPending} size="small" /></Grid>
<Grid item xs={12} sm={6}><TextField fullWidth label="Taxable Amount" value={editableTaxableAmount} onChange={(e) => { const val = e.target.value; setEditableTaxableAmount(val); const sum = (parseFloat(val) || 0) + (parseFloat(editableNonTaxableAmount) || 0); if (sum > 0) setEditableAmount(sum.toString()); hasChanges.current = true; }} onBlur={handleAutoSaveChanges} disabled={!isEditable || isActionPending} size="small" type="number"/></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Non-Taxable Amount" value={editableNonTaxableAmount} onChange={(e) => { const val = e.target.value; setEditableNonTaxableAmount(val); const sum = (parseFloat(editableTaxableAmount) || 0) + (parseFloat(val) || 0); if (sum > 0) setEditableAmount(sum.toString()); hasChanges.current = true; }} onBlur={handleAutoSaveChanges} disabled={!isEditable || isActionPending} size="small" type="number"/></Grid>
                        </Grid>
                        {/* Receipt Provided Select - disabled if not editable or action pending */}
                        <FormControl fullWidth size="small" sx={{ my: 2 }} disabled={!isEditable || isActionPending}>
                            <InputLabel>Receipt Provided (Taxable Status)</InputLabel>
                            <Select value={isReceiptProvided ? 'yes' : 'no'} label="Receipt Provided (Taxable Status)" onChange={(e) => { setIsReceiptProvided(e.target.value === 'yes'); hasChanges.current = true; }} onBlur={handleAutoSaveChanges}>
                                <MenuItem value="yes">Yes (Non-Taxable)</MenuItem>
                                <MenuItem value="no">No (Taxable)</MenuItem>
                            </Select>
                        </FormControl>
                        {/* Notes Field - disabled if action pending */}
                        <TextField fullWidth multiline rows={3} label="Reviewer Notes" value={notes} onChange={(e) => {setNotes(e.target.value); hasChanges.current = true;}} onBlur={handleAutoSaveChanges} disabled={!isNotesEditable} />
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );
  };

  // Main component render
  if (loading && !data) { // Show loading only on initial fetch
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error} <MuiButton size="small" onClick={() => { setReviewingBatchId(null); setActivePage('Claim History'); }}>Go Back</MuiButton></Alert></Box>;
  }

  if (!data) {
     return <Box sx={{ p: 3 }}><Alert severity="warning">Could not load claim data. <MuiButton size="small" onClick={() => { setReviewingBatchId(null); setActivePage('Claim History'); }}>Go Back</MuiButton></Alert></Box>;
  }


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 110px)', overflow: 'hidden' }}>
      {/* Header Section */}
      <Box sx={{ flexShrink: 0, p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <MuiButton startIcon={<ArrowBack />} onClick={() => { setReviewingBatchId(null); setActivePage('Claim History'); }}>Back to Claim History</MuiButton>
          {/* Batch Re-open Button (SuperAdmin only, if batch is finalized) */}
          {canReopenBatch && (<MuiButton variant="outlined" color="secondary" size="large" onClick={handleReopenClaimBatch} disabled={pendingActions['reopenBatch']} startIcon={pendingActions['reopenBatch'] ? <CircularProgress size={24} color="inherit" /> : <ReopenIcon />}>Re-open Claim Batch</MuiButton>)}
        </Box>
        <Typography variant="h4" gutterBottom sx={{ mt: 1,  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={data.claim_submission.employee_name ?? 'N/A'}>
            Reviewing Claim for {data.claim_submission.employee_name ?? 'N/A'}
        </Typography>
         {/* Display Batch Status */}
         <Chip label={`Batch Status: ${batchStatus.replace(/_/g, ' ')}`} size="small" variant="outlined" sx={{ mt: -1 }}/>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={2} sx={{ flex: 1, p: 1.5, overflow: 'hidden', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* Left Panel: Claim Items List */}
        <Grid item xs={12} md={3} sx={{ height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{ p: 1, flex: 1, overflowY: 'auto' }}>
                <Typography variant="h6" sx={{ p: 1 }}>Claim Items</Typography>
                <List dense>
                {data.claim_results.map((result: ClaimResult) => {
                    const originalItem = findOriginalItem(result, data.claim_items);
                    const isActioned = result.is_approved_by_reviewer !== null;
                    const isAiMatched = result.match_status === 'matched';
                    // Check pending state for *any* action on this item
                    const isActionPending = pendingActions[result.id] || pendingActions[`${result.id}_save`] || pendingActions[`${result.id}_reopen`];
                    let chipLabel = result.match_status.replace(/_/g, ' ');
                    let chipColor = statusChipColor(result.match_status);

                    // Determine chip label and color based on final status
                    if (isActioned) {
                        chipLabel = result.is_approved_by_reviewer ? 'Approved' : 'Rejected';
                        chipColor = result.is_approved_by_reviewer ? 'success' : 'error';
                    } else if (isAiMatched) {
                        chipLabel = 'AI Matched';
                        chipColor = 'success';
                    }

                    return (
                        <ListItem key={result.id} disablePadding>
                            <ListItemButton selected={selected?.result.id === result.id} onClick={() => handleSelectItem(result)}>
                                <ListItemText
                                    primary={originalItem?.description || 'N/A'}
                                    secondary={`Amount: ${formatCurrency(originalItem?.total_item_amount)}`}
                                    primaryTypographyProps={{ style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}}
                                />
                                {/* Show spinner if pending, otherwise show status chip */}
                                {isActionPending ? <CircularProgress size={20} sx={{ml: 1}}/> : <Chip label={chipLabel} color={chipColor} size="small" />}
                            </ListItemButton>
                        </ListItem>
                    );
                })}
                </List>
            </Paper>
        </Grid>

        {/* Middle Panel: Details and Unmatched Receipts */}
        <Grid item xs={12} md={5} sx={{ height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
             {/* Details Panel */}
             <Paper sx={{ p: 2, flexShrink: 0, overflowY: 'auto' }}>
                 {renderDetailsPanel()}
             </Paper>
            {/* Unmatched Receipts Panel - Conditionally rendered */}
            {/* Show if receipts exist, item selected, item needs link/intervention, AND batch isn't final */}
            {unmatchedReceipts.length > 0 && selected && (selected.result.match_status === 'unmatched' || selected.result.match_status === 'human_intervention') && !isBatchFinalized && (
                 <Paper sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
                     <Typography variant="h6">Available Unmatched Receipts</Typography>
                     {/* --- CHANGE: Replaced List with Checkbox List --- */}
                     <List dense>
                        {unmatchedReceipts.map(receipt => {
                          const isSelected = selectedReceipts.has(receipt.id);
                          return (
                            <ListItem
                              key={receipt.id}
                              disablePadding
                            >
                              <ListItemButton
                                onClick={() => {
                                  const newSet = new Set(selectedReceipts);
                                  if (isSelected) {
                                    newSet.delete(receipt.id);
                                  } else {
                                    newSet.add(receipt.id);
                                  }
                                  setSelectedReceipts(newSet);
                                }}
                              >
                                <ListItemIcon sx={{minWidth: 0, mr: 1.5}}>
                                  <Checkbox
                                    edge="start"
                                    checked={isSelected}
                                    tabIndex={-1}
                                    disableRipple
                                    size="small"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={receipt.file_name}
                                  secondary={`Amount: ${formatCurrency(receipt.extracted_data?.total)}`}
                                />
                                {/* Keep the preview button */}
                                <Tooltip title="Preview Receipt">
                                  <IconButton edge="end" onClick={(e) => { e.stopPropagation(); handlePreviewUnmatched(receipt.id); }}>
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                    </List>
                    {/* --- CHANGE: Added "Link Selected" Button --- */}
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{ mt: 1 }}
                      disabled={selectedReceipts.size === 0 || !!pendingActions[selected?.result.id!]}
                      onClick={handleOpenMultiLinkModal}
                    >
                      Link {selectedReceipts.size} Selected Receipt(s)
                    </Button>
                    {/* --- END CHANGE --- */}
                </Paper>
            )}
        </Grid>

        {/* Right Panel: Receipt Preview */}
        <Grid item xs={12} md={4} sx={{ height: { xs: 400, md: '100%' }, display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{ p: 1, flex: 1, overflow: 'hidden' }}>
                {receiptLoading ? (
                    <Box sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography color="text.secondary">Loading receipt preview...</Typography>
                    </Box>
                ) : receiptUrl ? (
                    <iframe src={receiptUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Receipt Preview" />
                ) : (
                    <Box sx={{p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                        <HelpOutline sx={{fontSize: 48, color: 'grey.400'}} />
                        <Typography color="text.secondary">
                            {selected && selected.result.receipt_file_path ? 'Could not load preview.' :
                             unmatchedReceipts.length > 0 && selected && (selected.result.match_status === 'unmatched' || selected.result.match_status === 'human_intervention') ? "Select an unmatched receipt to preview." :
                             "No receipt selected or available for preview."}
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Grid>
      </Grid>

      {/* --- CHANGE: Removed Single-Link Modal --- */}
      {/* <Dialog open={isSingleLinkModalOpen} ... > ... </Dialog> */}

      {/* --- CHANGE: Added new Multi-Link Modal --- */}
      <Dialog open={isMultiLinkModalOpen} onClose={handleCloseMultiLinkModal} fullWidth maxWidth="sm">
        <DialogTitle>Link Multiple Receipts</DialogTitle>
        <DialogContent>
            <Typography sx={{mb: 2, mt: 1}}>
                You are linking **{selectedReceipts.size}** receipt(s) to the claim item:
                <strong> "{selected?.original.description}"</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
                The system has calculated the total sum of these receipts. Please confirm or override this amount.
            </Typography>
            <TextField
                fullWidth
                label="Approved Amount"
                value={editableAmount}
                onChange={(e) => setEditableAmount(e.target.value)}
                type="number"
                sx={{ mt: 2 }}
            />
            <TextField
                fullWidth
                label="Taxable Amount"
                value={editableTaxableAmount}
                onChange={(e) => { const val = e.target.value; setEditableTaxableAmount(val); const sum = (parseFloat(val) || 0) + (parseFloat(editableNonTaxableAmount) || 0); if (sum > 0) setEditableAmount(sum.toString()); }}
                type="number"
                sx={{ mt: 2 }}
            />
            <TextField
                fullWidth
                label="Non-Taxable Amount"
                value={editableNonTaxableAmount}
                onChange={(e) => { const val = e.target.value; setEditableNonTaxableAmount(val); const sum = (parseFloat(editableTaxableAmount) || 0) + (parseFloat(val) || 0); if (sum > 0) setEditableAmount(sum.toString()); }}
                type="number"
                sx={{ mt: 2 }}
            />
            <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{mt: 2}}
            />
        </DialogContent>
        <DialogActions>
            {/* --- FIX: Corrected typo `MMuiButton` --- */}
            <MuiButton onClick={handleCloseMultiLinkModal} disabled={!!pendingActions[selected?.result.id!]}>Cancel</MuiButton>
            {/* --- FIX: Corrected typo `MMuiButton` and function call --- */}
            <MuiButton onClick={handleMultiLinkSubmit} variant="contained" disabled={!!pendingActions[selected?.result.id!]}>
                {pendingActions[selected?.result.id!] ? <CircularProgress size={24} /> : "Link and Approve"}
            </MuiButton>
        </DialogActions>
      </Dialog>
      {/* --- END CHANGE --- */}
    </Box>
  );
}